import { Box, Container, Stack, Text, Title } from "@mantine/core";
import { PAGE_HERO_HEIGHT } from "@/lib/heroLayout";

interface Props {
  title: string;
  subtitle?: string;
  /** CSS min-height. Defaults to a compact banner under the nav — much
   *  smaller than the home hero's 90vh so secondary routes don't make the
   *  visitor scroll past a huge title every navigation. */
  minHeight?: string;
}

/**
 * Compact full-bleed hero banner used at the top of secondary routes
 * (About, Releases, Live, Press, Contact). Same video backdrop as the home
 * hero so the brand visual is consistent everywhere, but condensed so
 * content sits closer to the fold.
 *
 * Detail routes (releases/:slug, live/:slug) skip this because they have
 * their own visual identity (cover art / embedded video). Form routes
 * (access, private/*) also skip it.
 */
export function PageHero({
  title,
  subtitle,
  minHeight = PAGE_HERO_HEIGHT,
}: Props) {
  return (
    <Box
      component="section"
      className="hero-bleed"
      style={{
        position: "relative",
        minHeight,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }}
    >
      {/* Video backdrop lives in the persistent layer behind this section
          (AppShell → PersistentHeroBackdrop), shared with the home hero so it
          never remounts on navigation. This box just reserves the band. */}
      <Container
        size="xl"
        px={{ base: "md", md: "xl" }}
        py="xl"
        style={{ position: "relative", zIndex: 1, width: "100%" }}
      >
        <Stack className="heading-fade-in" gap="xs" align="center" ta="center">
          <Title
            order={1}
            fz="clamp(2rem, 7vw, 4.5rem)"
            fw={700}
            className="iridescent-text"
            style={{
              letterSpacing: "0.04em",
              lineHeight: 1.05,
              margin: 0,
              // Brand-wide rule: secondary-route page heroes render uppercase
              // (matches the "7 MINUTES DEAD" home hero treatment). Apply via
              // CSS so route code keeps passing sentence-case strings.
              textTransform: "uppercase",
            }}
          >
            {title}
          </Title>
          {subtitle && (
            <Text size="md" c="var(--color-starlight)" maw={640} ta="center">
              {subtitle}
            </Text>
          )}
        </Stack>
      </Container>
    </Box>
  );
}
